import { mapContextualSegmentsToFormState } from '@openx/components-targeting/Targeting/ContextualSegments/helpers.ts';
import { contextualSegmentApiKey } from '@openx/types/targeting/contextualSegments.ts';
import type { UrlTargeting } from '@openx/types/targeting/domains.ts';
import type { Targeting } from '@openx/types/targeting/targeting.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { isObjectRecord } from '@openx/utils/lib/typeGuards/isObjectRecord.ts';

export type TargetingBlockingRulesInput = Omit<Partial<Targeting>, 'rendering_context'> & {
  rendering_context?: Partial<NonNullable<Targeting['rendering_context']>> | null;
  [TargetingFields.URL_TARGETING]?: Partial<UrlTargeting> | null;
  [TargetingFields.CATEGORY]?: unknown;
  [TargetingFields.AD_SIZES]?: unknown;
  [TargetingFields.CUSTOM_VARIABLES]?: unknown;
  [TargetingFields.CONTEXTUAL_SEGMENTS]?: unknown;
};

type TargetingRulesInput = {
  url_targeting?: Partial<UrlTargeting> | null;
  targeting: {
    rendering_context?: Partial<NonNullable<Targeting['rendering_context']>> | null;
    video?: Targeting['video'] | null;
    instream_content?: Targeting['instream_content'] | null;
    viewability?: Targeting['viewability'] | null;
    vtr?: Targeting['vtr'] | null;
    content?: Targeting['content'] | null;
    technographic?: Targeting['technographic'] | null;
    metacategory?: unknown;
    app_inventory?: Targeting['app_inventory'] | null;
    audience?: Targeting['audience'] | null;
    geographic?: Targeting['geographic'] | null;
    categories?: unknown;
    inventory_format?: Targeting['inventory_format'] | null;
    ads?: unknown;
    custom?: unknown;
    contextual?: unknown;
  };
};

const hasUrlTargetingValues = (urlTargeting: Partial<UrlTargeting> | null | undefined): boolean =>
  Boolean(urlTargeting?.inventorylist_id || urlTargeting?.urls?.length);

const hasMetacategoryKeywordValues = (metacategory: Targeting['metacategory'] | null | undefined): boolean =>
  Boolean(metacategory?.keywords?.includes?.length || metacategory?.keywords?.excludes?.length);

const normalizeMetacategory = (metacategory: Targeting['metacategory'] | null | undefined): unknown => {
  if (!metacategory) {
    return null;
  }

  if (hasMetacategoryKeywordValues(metacategory)) {
    return metacategory;
  }

  const { keywords: _keywords, ...metacategoryWithoutEmptyKeywords } = metacategory;

  return metacategoryWithoutEmptyKeywords;
};

const normalizeCustomTargeting = (customVariablesValue: unknown): unknown => {
  if (!isObjectRecord(customVariablesValue)) {
    return customVariablesValue ?? null;
  }

  return customVariablesValue.custom ?? customVariablesValue ?? null;
};

/** The package form validates its API payload, where contextual segments are stored under `contextual_segment`. */
const normalizeContextualSegment = (contextualSegment: Targeting['contextual_segment']): unknown => {
  const contextual = mapContextualSegmentsToFormState(contextualSegment);

  return contextual.length ? contextual : null;
};

export const normalizeTargetingForSchema = (
  targeting: TargetingBlockingRulesInput | null | undefined
): TargetingRulesInput => {
  const customVariablesValue = targeting?.[TargetingFields.CUSTOM_VARIABLES];
  const contextualSegmentsValue = targeting?.[TargetingFields.CONTEXTUAL_SEGMENTS];
  const urlTargeting = targeting?.[TargetingFields.URL_TARGETING];

  const normalizedUrlTargeting = hasUrlTargetingValues(urlTargeting) ? (urlTargeting ?? null) : null;

  const custom = normalizeCustomTargeting(customVariablesValue);
  const contextual = contextualSegmentsValue ?? normalizeContextualSegment(targeting?.[contextualSegmentApiKey]);

  return {
    url_targeting: normalizedUrlTargeting,
    targeting: {
      rendering_context: targeting?.rendering_context ?? null,
      video: targeting?.video ?? null,
      instream_content: targeting?.instream_content ?? null,
      viewability: targeting?.viewability ?? null,
      vtr: targeting?.vtr ?? null,
      content: targeting?.content ?? null,
      technographic: targeting?.technographic ?? null,
      metacategory: normalizeMetacategory(targeting?.metacategory),
      app_inventory: targeting?.app_inventory ?? null,
      audience: targeting?.audience ?? null,
      geographic: targeting?.geographic ?? null,
      categories: targeting?.[TargetingFields.CATEGORY] ?? null,
      inventory_format: targeting?.inventory_format ?? null,
      ads: targeting?.[TargetingFields.AD_SIZES] ?? null,
      custom,
      contextual,
    },
  };
};
